// One line of text, with the whole value on hover.

import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';

interface TruncatedTextProps {
  text: string;
  /** Classes for the visible line. */
  className?: string;
  /** Shorter than this never clips, so it skips the tooltip. */
  minLength?: number;
  /** Opens the text as a link. */
  href?: string;
  /** Gap between the text and its tooltip. */
  sideOffset?: number;
  /** Puts a copy button beside the line, in a gutter the row keeps whether or not you hover. */
  copyable?: boolean;
}

// Tool output carries bare urls, and an agent expects to open one.
const HTTP_URL = /^https?:\/\/\S+$/;

// Long enough that passing over a list never opens a tooltip.
const HOVER_DELAY_MS = 400;

/** Whether the line is cut short; scrollWidth rounds to whole pixels, so a fractional overflow needs the exact text width. */
function isClipped(line: HTMLElement): boolean {
  if (line.scrollWidth > line.clientWidth) return true;
  const range = document.createRange();
  range.selectNodeContents(line);
  return (range.getBoundingClientRect?.().width ?? 0) > line.getBoundingClientRect().width;
}

export function TruncatedText({ text, className, minLength = 16, href, sideOffset = 8, copyable }: TruncatedTextProps) {
  const lineRef = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [hovering, setHovering] = useState(false);
  const target = href ?? (HTTP_URL.test(text) ? text : null);
  const long = text.length >= minLength;

  const cancel = () => clearTimeout(timer.current);
  // One tooltip per row would be hundreds in a list, so it waits for the pointer to stop.
  const reveal = () => {
    cancel();
    timer.current = setTimeout(() => {
      const line = lineRef.current;
      if (line && isClipped(line)) setHovering(true);
    }, HOVER_DELAY_MS);
  };
  useEffect(() => cancel, []);

  const waiting = long && !hovering;
  const lineProps = {
    className: cn('truncate', className),
    'data-truncatable': long || undefined,
    onPointerEnter: waiting ? reveal : undefined,
    onPointerLeave: waiting ? cancel : undefined,
    onPointerDown: waiting ? cancel : undefined,
    onFocus: waiting ? reveal : undefined,
    onBlur: waiting ? cancel : undefined,
  };

  // Link colours go last so a caller's text colour cannot swallow them.
  const line = target ? (
    <a
      {...lineProps}
      ref={lineRef as React.Ref<HTMLAnchorElement>}
      href={target}
      target="_blank"
      rel="noreferrer"
      className={cn(lineProps.className, 'text-blue-500 hover:underline')}
    >
      {text}
    </a>
  ) : (
    <span {...lineProps} ref={lineRef as React.Ref<HTMLSpanElement>}>
      {text}
    </span>
  );
  // The button keeps its place in the row, so appearing on hover shifts nothing.
  const withCopy = (content: React.ReactNode) =>
    copyable ? (
      <span className="group/copy flex min-w-0 items-center gap-1">
        {content}
        <button
          type="button"
          title="Copy"
          aria-label={`Copy ${text}`}
          onClick={() => {
            void navigator.clipboard?.writeText(text);
            toast.success('Copied to clipboard');
          }}
          className="shrink-0 cursor-pointer text-gray-700 opacity-0 transition-opacity group-hover/copy:opacity-100 focus-visible:opacity-100 hover:text-text-body motion-reduce:transition-none"
        >
          <Copy className="size-3" />
        </button>
      </span>
    ) : (
      content
    );

  if (!long || !hovering) return withCopy(line);

  return withCopy(
    // Closes on leave: a hoverable tooltip waits for pointer moves an email iframe swallows, so it stuck open.
    <Tooltip defaultOpen disableHoverableContent onOpenChange={(open) => !open && setHovering(false)}>
      <TooltipTrigger asChild>{line}</TooltipTrigger>
      <TooltipContent variant="dark" sideOffset={sideOffset} className="max-w-[300px] text-wrap break-words shadow-[0_3px_12px_rgba(0,0,0,0.08)]">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
